import { blogCategories, blogAuthors, blogTags, blogFaqs } from "@/mocks/blogMeta";
import { blogPosts1 } from "@/mocks/blogPosts1";
import { blogPosts2 } from "@/mocks/blogPosts2";
import { blogPosts3 } from "@/mocks/blogPosts3";
import { blogPosts4 } from "@/mocks/blogPosts4";
import { blogPosts5 } from "@/mocks/blogPosts5";

export interface BlogBlock {
  id: string;
  heading: string;
  level: 2 | 3;
  paragraphs?: string[];
  list?: string[];
}

export interface BlogFaq {
  question: string;
  answer: string;
}

export interface BlogCta {
  title: string;
  text: string;
  label: string;
  to: string;
}

export interface BlogCategory {
  slug: string;
  name: string;
  shortName: string;
  description: string;
  longDescription: string;
  icon: string;
  accent: "primary" | "accent" | "secondary";
  topics: string[];
  /** Imagem de compartilhamento (Open Graph) específica da categoria. */
  ogImage?: string;
  /** Perguntas frequentes visíveis na página (gera dados estruturados FAQPage). */
  faqs?: BlogFaq[];
  collection?: boolean;
}

export interface BlogPost {
  slug: string;
  title: string;
  h1: string;
  excerpt: string;
  metaTitle: string;
  metaDescription: string;
  focusKeyword: string;
  keywords: string[];
  category: string;
  level: "Iniciante" | "Intermediário" | "Avançado";
  tags: string[];
  author: string;
  publishedAt: string;
  updatedAt: string;
  readingTime: string;
  image: string;
  imageAlt: string;
  intro: string[];
  content: BlogBlock[];
  faqs: BlogFaq[];
  cta: BlogCta;
}

export const categories: BlogCategory[] = blogCategories as unknown as BlogCategory[];
export const tags: string[] = blogTags;
export const generalFaqs: BlogFaq[] = blogFaqs as unknown as BlogFaq[];

export const posts: BlogPost[] = [
  ...blogPosts1,
  ...blogPosts2,
  ...blogPosts3,
  ...blogPosts4,
  ...blogPosts5,
] as unknown as BlogPost[];

export const authors = blogAuthors as unknown as Record<
  string,
  { id: string; name: string; role: string; bio: string; avatar: string }
>;

/** Categorias reais (sem a coleção de guias). */
export const contentCategories = categories.filter((cat) => !cat.collection);

const POPULAR_SLUGS = [
  "o-que-e-hospedagem-de-sites",
  "o-que-e-seo",
  "como-criar-um-site",
  "o-que-e-vps",
  "o-que-e-mysql",
  "o-que-e-cpanel",
];

function byDateDesc(a: BlogPost, b: BlogPost) {
  return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
}

export function getCategory(slug: string): BlogCategory | undefined {
  return categories.find((cat) => cat.slug === slug);
}

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((post) => post.slug === slug);
}

export function getAuthor(id: string) {
  return authors[id] ?? authors.nxhost;
}

export function getPostsByCategory(slug: string): BlogPost[] {
  return posts.filter((post) => post.category === slug).sort(byDateDesc);
}

export function getRecentPosts(limit?: number): BlogPost[] {
  const sorted = [...posts].sort(byDateDesc);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getFeaturedPosts(limit = 6): BlogPost[] {
  return getRecentPosts(limit);
}

export function getPopularPosts(limit = 6): BlogPost[] {
  const picked = POPULAR_SLUGS.map((slug) => getPost(slug)).filter(
    (post): post is BlogPost => Boolean(post),
  );
  const extra = getRecentPosts().filter((post) => !POPULAR_SLUGS.includes(post.slug));
  return [...picked, ...extra].slice(0, limit);
}

export function getRelatedPosts(post: BlogPost, limit = 3): BlogPost[] {
  const sameCategory = posts
    .filter((item) => item.slug !== post.slug && item.category === post.category)
    .sort(byDateDesc);
  const sameTag = posts
    .filter(
      (item) =>
        item.slug !== post.slug &&
        item.category !== post.category &&
        item.tags.some((tag) => post.tags.includes(tag)),
    )
    .sort(byDateDesc);
  const others = posts
    .filter((item) => item.slug !== post.slug)
    .sort(byDateDesc)
    .filter((item) => !sameCategory.includes(item) && !sameTag.includes(item));
  return [...sameCategory, ...sameTag, ...others].slice(0, limit);
}

export interface ThemeGroup {
  tag: string;
  posts: BlogPost[];
}

/**
 * Agrupa artigos relacionados por tema (tag), gerando links internos automáticos.
 * Reforça a linkagem interna e o SEO sem depender de curadoria manual.
 */
export function getRelatedByTheme(
  post: BlogPost,
  maxThemes = 3,
  maxPerTheme = 5,
): ThemeGroup[] {
  const groups: ThemeGroup[] = [];
  const used = new Set<string>();

  const byTag = post.tags
    .map((tag) => ({
      tag,
      items: posts
        .filter((item) => item.slug !== post.slug && item.tags.includes(tag))
        .sort(byDateDesc),
    }))
    .filter((group) => group.items.length >= 2)
    .sort((a, b) => b.items.length - a.items.length);

  byTag.forEach((group) => {
    if (groups.length >= maxThemes) return;
    const fresh = group.items.filter((item) => !used.has(item.slug)).slice(0, maxPerTheme);
    if (fresh.length >= 2) {
      fresh.forEach((item) => used.add(item.slug));
      groups.push({ tag: group.tag, posts: fresh });
    }
  });

  if (groups.length < maxThemes) {
    const category = getCategory(post.category);
    const fresh = posts
      .filter(
        (item) =>
          item.slug !== post.slug && item.category === post.category && !used.has(item.slug),
      )
      .sort(byDateDesc)
      .slice(0, maxPerTheme);
    if (fresh.length >= 2) {
      fresh.forEach((item) => used.add(item.slug));
      groups.push({ tag: category?.shortName ?? "Mesma categoria", posts: fresh });
    }
  }

  return groups.slice(0, maxThemes);
}

export function searchPosts(query: string): BlogPost[] {
  const term = query.trim().toLowerCase();
  if (!term) return [];
  const words = term.split(/\s+/).filter((word) => word.length > 2);
  return posts
    .map((post) => {
      const category = getCategory(post.category);
      const haystack = [
        post.title,
        post.excerpt,
        post.focusKeyword,
        post.keywords.join(" "),
        post.tags.join(" "),
        category?.name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      let score = 0;
      if (post.title.toLowerCase().includes(term)) score += 5;
      if (post.focusKeyword.toLowerCase().includes(term)) score += 4;
      words.forEach((word) => {
        if (haystack.includes(word)) score += 1;
        if (post.title.toLowerCase().includes(word)) score += 2;
      });
      return { post, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.post);
}

export function getTagsWithCount(): { tag: string; count: number }[] {
  return tags
    .map((tag) => ({ tag, count: posts.filter((post) => post.tags.includes(tag)).length }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count);
}

export function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}

export function getGuidesByLevel(level: string): BlogPost[] {
  const sorted = getRecentPosts();
  if (level === "Todos") return sorted;
  return sorted.filter((post) => post.level === level);
}

export const GUIDE_LEVELS = ["Iniciante", "Intermediário", "Avançado"];